import { presentLegalAcceptance } from "./legal_acceptance.js"
import { findOfflinePdf, pauseOfflinePdfs, reauthorizeOfflinePdf, saveOfflineProgress, syncOfflineProgress } from "./offline_pdf_access.js"
import { withOfflineCleanupLock } from "./offline_download_lock.js"
import { OfflinePdfStore, offlineCopyExpired } from "./offline_pdf_store.js"

const MAX_TIMEOUT_DURATION = 2_147_483_647

// Owns the local-copy lifecycle. The reader controller remains responsible for
// PDF rendering and translates these callbacks into visible reader state.
export default class PdfOfflineSession {
  static forReader({ readOnly, createStore = () => new OfflinePdfStore(), ...options }) {
    if (readOnly) return null

    return new this({ ...options, store: createStore() })
  }

  constructor({
    userId,
    bookId,
    editionId,
    manifestUrl,
    store = new OfflinePdfStore(),
    modeChanged = () => {},
    storageUnavailable = () => {},
    loadOnlinePanels = async () => {},
    invalidateReader = () => window.location.reload(),
    closeReader = () => {}
  }) {
    this.userId = userId
    this.bookId = bookId
    this.editionId = editionId
    this.manifestUrl = manifestUrl
    this.store = store
    this.modeChanged = modeChanged
    this.storageUnavailable = storageUnavailable
    this.loadOnlinePanels = loadOnlinePanels
    this.invalidateReader = invalidateReader
    this.closeReader = closeReader
    this.record = null
    this.offline = false
    this.superseded = false
  }

  async connect() {
    const connection = Symbol("pdf-offline-session")
    this.connection = connection
    this.legalAcceptanceListener = (event) => this.pauseForLegalAcceptance(event.detail, false)
    window.addEventListener("legal-acceptance:required", this.legalAcceptanceListener)
    this.startExpiryMonitoring()
    await this.prepareSafely(connection)
  }

  disconnect() {
    this.connection = null
    window.removeEventListener("legal-acceptance:required", this.legalAcceptanceListener)
    this.stopExpiryMonitoring()
    window.clearTimeout(this.syncRetryTimer)
    this.syncRetryTimer = null
    this.store.closeDatabase()
  }

  async prepare(connection = this.connection) {
    if (!this.currentConnection(connection)) return

    if (!this.store.supported()) {
      this.setMode(false)
      return
    }

    await this.store.setActiveUser(this.userId)
    if (!this.currentConnection(connection)) return
    const staleRecords = await this.store.selectActiveEdition({
      userId: this.userId,
      bookId: this.bookId,
      editionId: this.editionId
    })
    if (!this.currentConnection(connection)) return
    const records = await this.store.list(this.userId)
    if (!this.currentConnection(connection)) return

    if (this.hasOlderEdition(staleRecords, records)) this.cleanOlderEditions()

    const record = await findOfflinePdf({
      store: this.store,
      userId: this.userId,
      editionId: this.editionId
    })
    if (!this.currentConnection(connection)) return
    this.record = record
    this.scheduleExpiry()
    if (!this.record) {
      this.setMode(false)
      return
    }

    const access = await reauthorizeOfflinePdf(this.record, this.manifestUrl, { store: this.store })
    if (!this.currentConnection(connection)) return
    this.record = access.record
    this.scheduleExpiry()
    if (access.status === "legal_acceptance_required" || access.record?.legalAcceptanceRequired) {
      this.pauseForLegalAcceptance(access)
      return
    }
    if (access.status === "offline") {
      this.setMode(true)
      this.scheduleSyncRetryIfOnline()
      return
    }
    if (access.status !== "authorized") {
      if (this.handleInactiveRecord(access.status)) return
      this.setMode(false)
      if (access.status === "retry") this.scheduleSyncRetry()
      return
    }

    await this.finishAuthorizedReconnect({ loadPanels: false, connection })
  }

  async prepareSafely(connection = this.connection) {
    try {
      await this.prepare(connection)
    } catch (error) {
      if (!this.currentConnection(connection)) return

      // Local storage is an enhancement and cannot prevent authorized network reading.
      this.record = null
      this.setMode(false)
      this.storageUnavailable()
      console.warn("Offline PDF storage is unavailable; continuing with network reading", error)
    }
  }

  reconnect() {
    if (this.reconnectPromise) return this.reconnectPromise

    const connection = this.connection
    this.reconnectPromise = this.performReconnect(connection).finally(() => {
      this.reconnectPromise = null
    })
    return this.reconnectPromise
  }

  async performReconnect(connection = this.connection) {
    if (!this.record || !this.currentConnection(connection)) return

    const access = await reauthorizeOfflinePdf(this.record, this.manifestUrl, { store: this.store })
    if (!this.currentConnection(connection)) return
    this.record = access.record
    this.scheduleExpiry()
    if (access.status === "legal_acceptance_required" || access.record?.legalAcceptanceRequired) {
      this.pauseForLegalAcceptance(access)
    } else if (access.status === "authorized") {
      if (this.legalAcceptanceBlocked) { this.invalidateReader(); return }
      await this.finishAuthorizedReconnect({ connection })
    } else if (["denied", "expired", "stale"].includes(access.status)) {
      this.invalidateReader()
    } else if (this.handleInactiveRecord(access.status)) {
      return
    } else if (access.status === "offline") {
      this.scheduleSyncRetryIfOnline()
    } else if (access.status === "retry") {
      this.scheduleSyncRetry()
    }
  }

  disconnected() {
    if (this.record && !this.legalAcceptanceBlocked) this.setMode(true)
  }

  async saveProgress(progress, saveOnline) {
    if (this.superseded) return

    if ((this.offline || this.legalAcceptanceBlocked) && this.record) {
      const result = await saveOfflineProgress(this.record, progress, { store: this.store })
      this.handleInactiveRecord(result.status)
      return
    }

    if (this.record) {
      const active = await this.store.activeRecord(this.record)
      if (this.handleInactiveRecord(active.status)) return
      Object.assign(this.record, active.record)
    }

    const saved = await saveOnline()
    if (!saved && this.record) {
      const result = await saveOfflineProgress(this.record, progress, { store: this.store })
      if (this.handleInactiveRecord(result.status)) return
      this.setMode(true)
      this.scheduleSyncRetry()
    } else if (saved && this.record) {
      const result = await this.store.saveProgressIfActive(this.record, progress)
      if (this.handleInactiveRecord(result.status)) return
      Object.assign(this.record, result.record)
    }
  }

  async savePreferences(attributes) {
    if (!this.offline || !this.record) return false

    const result = await this.store.putIfActive({ ...this.record, ...attributes })
    if (this.handleInactiveRecord(result.status)) return true
    Object.assign(this.record, result.record)
    return true
  }

  async syncPosition() {
    if (!this.record?.pendingProgress) return "idle"

    const result = await syncOfflineProgress(this.record, { store: this.store })
    this.record = result.record
    if (result.status === "legal_acceptance_required") {
      this.pauseForLegalAcceptance(result)
    } else if (["pending", "retry"].includes(result.status)) {
      this.scheduleSyncRetry()
    } else if (result.status === "denied") {
      this.invalidateReader()
    } else {
      this.handleInactiveRecord(result.status)
    }
    return result.status
  }

  async finishAuthorizedReconnect({ loadPanels = true, connection = this.connection } = {}) {
    if (!this.currentConnection(connection)) return

    if (this.record?.pendingProgress) {
      this.setMode(true)
      const status = await this.syncPosition()
      if (!this.currentConnection(connection)) return
      if (!this.record || ["legal_acceptance_required", "denied", "missing", "pending", "retry", "superseded"].includes(status)) return
    }

    this.setMode(false)
    if (loadPanels) {
      await this.loadOnlinePanels()
      if (!this.currentConnection(connection)) return
    }
  }

  pauseForLegalAcceptance(result, present = true) {
    this.legalAcceptanceBlocked = true
    window.clearTimeout(this.syncRetryTimer)
    this.syncRetryTimer = null
    this.setMode(true)
    this.closeReader()
    if (this.store.supported?.() !== false) {
      void pauseOfflinePdfs(this.store, this.record || { userId: this.userId }, result.acceptanceUrl).catch((error) => console.warn("Unable to persist consent pause", error))
    }
    if (present) presentLegalAcceptance(result)
  }

  setMode(offline) {
    this.offline = offline
    this.modeChanged(offline)
  }

  handleInactiveRecord(status) {
    if (!["missing", "superseded"].includes(status)) return false
    if (this.superseded) return true

    this.superseded = true
    this.record = null
    window.clearTimeout(this.syncRetryTimer)
    this.syncRetryTimer = null
    this.invalidateReader()
    return true
  }

  scheduleSyncRetry() {
    if (this.legalAcceptanceBlocked) return
    window.clearTimeout(this.syncRetryTimer)
    this.syncRetryTimer = window.setTimeout(() => this.reconnect(), 5000)
  }

  scheduleSyncRetryIfOnline() {
    if (navigator.onLine) this.scheduleSyncRetry()
  }

  startExpiryMonitoring() {
    if (this.expiryCheck) return

    this.expiryCheck = async () => {
      if (document.visibilityState === "hidden") return
      if (!await this.enforceExpiry()) this.scheduleExpiry()
    }
    document.addEventListener("visibilitychange", this.expiryCheck)
    window.addEventListener("focus", this.expiryCheck)
    window.addEventListener("pageshow", this.expiryCheck)
    this.scheduleExpiry()
  }

  stopExpiryMonitoring() {
    window.clearTimeout(this.expiryTimer)
    this.expiryTimer = null
    if (!this.expiryCheck) return

    document.removeEventListener("visibilitychange", this.expiryCheck)
    window.removeEventListener("focus", this.expiryCheck)
    window.removeEventListener("pageshow", this.expiryCheck)
    this.expiryCheck = null
  }

  scheduleExpiry(now = Date.now()) {
    window.clearTimeout(this.expiryTimer)
    this.expiryTimer = null
    if (!this.record?.expiresAt) return

    const expiration = Date.parse(this.record.expiresAt)
    const delay = expiration - now
    if (!Number.isFinite(delay) || delay <= 0) {
      void this.enforceExpiry(now)
      return
    }

    this.expiryTimer = window.setTimeout(async () => {
      if (!await this.enforceExpiry()) this.scheduleExpiry()
    }, Math.min(delay, MAX_TIMEOUT_DURATION))
  }

  async enforceExpiry(now = Date.now()) {
    if (!offlineCopyExpired(this.record, now)) return false
    if (this.expiryPromise) return this.expiryPromise

    const expiredRecord = this.record
    this.record = null
    window.clearTimeout(this.expiryTimer)
    this.expiryTimer = null
    this.closeReader()

    this.expiryPromise = (async () => {
      try {
        await this.store.delete(expiredRecord)
      } catch (error) {
        console.error("Unable to delete expired offline PDF", error)
      } finally {
        this.invalidateReader()
      }
      return true
    })()
    return this.expiryPromise
  }

  currentConnection(connection) {
    return Boolean(connection) && this.connection === connection
  }

  hasOlderEdition(staleRecords, records) {
    return staleRecords.length > 0 || records.some((record) => (
      record.book?.id === this.bookId && record.editionId !== this.editionId
    ))
  }

  cleanOlderEditions() {
    // Selection already revoked older copies. Physical cleanup can wait for a
    // downloader holding the lock without delaying this authorized reader.
    void withOfflineCleanupLock(() => (
      this.store.deleteOtherEditionsForBook({
        userId: this.userId,
        bookId: this.bookId,
        editionId: this.editionId
      })
    )).then((cleaned) => {
      if (!cleaned) throw new Error("Offline cleanup coordination is unavailable")
    }).catch((error) => console.warn("Unable to clean up an older offline edition", error))
  }
}
